import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Pagination, useAnalyticsPageView, usePathParam } from '@chilecompra/react-kit'
import {
  Button,
  Dialog,
  Skeleton,
  Table,
  TableRow,
  Toast,
  Typography,
  usePagination
} from '@chilecompra/react-kit/components'
import { onCloseHistoricalContractsDialog, onGetHistoricalContractsThunk } from './HistoricalContracts.actions'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { Column, DialogButtons, ExternalLink, Row, Strong, TableCell } from './HistoricalContracts.styles'
import { formatDate } from '../../modules/utils/formatters'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { REACT_APP_MERCADO_PUBLICO_BASE_URL } from '../../config/settings/environment'
import { DialogContent } from '@mui/material'
import { DEFAULT_ROWS_PER_PAGE, USER_TYPE } from '../../config/settings/constant'
import useReCaptcha from '../../modules/hooks/reCaptcha'

/**
 * The HistoricalContractsDialog's container.
 */
const HistoricalContractsDialog = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { contracts, error, loading, open } = useSelector(state => state.historicalContracts)
  const { months } = useSelector(state => state.contractualBehavior)

  const handleClose = () => {
    dispatch(onCloseHistoricalContractsDialog())
  }

  const { page, totalPages, records, onChangePage, onGetCurrentPage } = usePagination({
    data: contracts,
    initialPage: 1,
    initialRowsPerPage: DEFAULT_ROWS_PER_PAGE
  })

  useEffect(() => {
    if (open === false || !loading || userType === '') return
    if (userType === USER_TYPE.PUBLIC) {
      const executeAll = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        dispatch(onGetHistoricalContractsThunk({ dni }))
      }
      executeAll()
    } else {
      dispatch(onGetHistoricalContractsThunk({ dni }))
    }
  }, [open, userType])

  useAnalyticsPageView('/ficha/contrato/historico')
  return (
    <Dialog maxWidth="800px" open={open} onClose={handleClose}>
      <DialogContent>
        <Typography variant="h2" fontWeight="light" align="left" margin="0 0 30px 0">
          Contratos evaluados históricos
        </Typography>
        <Row margin="14px 0 0">
          <Column xs={12}>
            {!error && loading && (
              <>
                <Skeleton width="100%" />
                <Skeleton width="75%" />
              </>
            )}
            {!error && !loading && (
              <Typography variant="body2">
                {`Estos son los contratos del proveedor que han superado ${
                  months === 1 ? 'el último mes' : `los ${months} meses`
                } desde la fecha de aceptación de la orden
            de compra. `}
                Estos contratos <Strong>no son considerados en la nota de evaluación por contratos</Strong> del
                proveedor.
              </Typography>
            )}

            {loading && !error && <TablePlaceholder margin="28px 0 8px" />}

            {!loading && !error && (
              <Table accessibility={{ label: 'Sanciones históricas' }} margin="28px 0 8px">
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="bold" variant="caption">
                      Id contrato
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold" variant="caption">
                      Nombre contrato
                    </Typography>
                  </TableCell>
                  <TableCell width="110px">
                    <Typography fontWeight="bold" variant="caption">
                      Fecha evaluación
                    </Typography>
                  </TableCell>
                  <TableCell width="110px">
                    <Typography fontWeight="bold" variant="caption">
                      Nota
                    </Typography>
                  </TableCell>
                  {userType !== USER_TYPE.PUBLIC && (
                    <TableCell width="70px">
                      <Typography fontWeight="bold" variant="caption">
                        Acciones
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
                {onGetCurrentPage().map((contract, index) => {
                  return (
                    <TableRow key={`sanction-${index}`}>
                      <TableCell>
                        <Strong display="block">{contract.dni}</Strong>
                      </TableCell>
                      <TableCell>
                        <Strong>{contract.contractName}</Strong>
                      </TableCell>
                      <TableCell width="110px">{formatDate(contract.publicationDate)}</TableCell>
                      <TableCell width="110px">{contract.contractScore}</TableCell>
                      {userType !== USER_TYPE.PUBLIC && (
                        <TableCell width="70px">
                          <ExternalLink
                            href={`${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Contratos/GestionContratos/DetalleContrato?idContrato=${contract.contractId}`}
                            target="_blank"
                          >
                            Ver detalle
                          </ExternalLink>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}

                {contracts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan="5">
                      <Typography align="center" color="black3" variant="body2">
                        Este proveedor no registra evaluaciones de contratos históricos.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </Table>
            )}

            {contracts?.length > DEFAULT_ROWS_PER_PAGE && !loading && (
              <Pagination
                size="small"
                alignment="right"
                total={contracts?.length}
                count={totalPages}
                page={page}
                onChange={onChangePage}
                records={records}
                disableFirstButton={false}
                disableLastButton={false}
                disableResults={false}
                disabled={loading}
                labels={{
                  of: 'de',
                  results: 'resultados',
                  showing: 'Mostrando'
                }}
              />
            )}

            {!loading && error && (
              <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
                En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
                minutos más.
              </Toast>
            )}
            <DialogButtons>
              <Button disabled={loading} color="default" margin="0 43px 0 0" onClick={handleClose} variant="contained">
                Cerrar
              </Button>
            </DialogButtons>
          </Column>
        </Row>
      </DialogContent>
    </Dialog>
  )
}

export default HistoricalContractsDialog
